/**
 * E-Invoice Turnover Validation & One-Way Gate Engine
 * InvenPro ERP — Digi India Solutions
 *
 * Rules (Notification No. 13/2020 – Central Tax):
 * 1. E-Invoice is mandatory for companies with annual turnover >= ₹5 Crore.
 * 2. One-Way Gate: Once turnover crosses ₹5 Crore in any FY, E-Invoice is ALWAYS mandatory forever.
 * 3. Manual Override: Admin can toggle E-Invoice mandatory if GSTN mandates but system shows exempt.
 */

export const E_INVOICE_TURNOVER_THRESHOLD = 50000000; // ₹5 Crore (50,000,000)

export type EInvoiceStatus = 'MANDATORY' | 'EXEMPT' | 'MANUAL_OVERRIDE';

export interface EInvoiceEligibilityResult {
  isMandatory: boolean;
  status: EInvoiceStatus;
  statusLabel: string;
  reason: string;
  oneWayGateActive: boolean;
  threshold: number;
  percentageOfThreshold: number;
  formattedPreviousTurnover: string;
  formattedThreshold: string;
}

export function formatINR(n?: number | null): string {
  if (n == null || isNaN(Number(n))) return '₹0';
  return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

export function formatCr(n?: number | null): string {
  if (n == null || isNaN(Number(n))) return '₹0.00 Cr';
  const val = Number(n) / 10000000;
  return `₹${val.toFixed(2)} Cr`;
}

export function calculateEInvoiceEligibility(params: {
  previousFyTurnover?: number;
  currentFyEstTurnover?: number;
  einvoiceEverApplicable?: boolean;
  einvoiceManualOverride?: boolean;
  einvoiceApplicableFrom?: string | null;
}): EInvoiceEligibilityResult {
  const threshold = E_INVOICE_TURNOVER_THRESHOLD;
  const prevTurnover = Number(params.previousFyTurnover || 0);
  const wasEverApplicable = Boolean(params.einvoiceEverApplicable);
  const manualOverride = Boolean(params.einvoiceManualOverride);

  const percentage = Math.min(100, Math.round((prevTurnover / threshold) * 100));

  // 1. One-Way Gate: Previously crossed ₹5 Crore
  if (wasEverApplicable) {
    return {
      isMandatory: true,
      status: 'MANDATORY',
      statusLabel: 'Mandatory (One-Way Gate Locked)',
      reason: 'E-Invoice is permanently mandatory under GST Notification No. 13/2020 because turnover crossed ₹5 Crore in a prior year.',
      oneWayGateActive: true,
      threshold,
      percentageOfThreshold: percentage,
      formattedPreviousTurnover: formatINR(prevTurnover),
      formattedThreshold: '₹5.00 Cr (₹50,000,000)',
    };
  }

  // 2. Turnover >= 5 Crore in Previous FY
  if (prevTurnover >= threshold) {
    return {
      isMandatory: true,
      status: 'MANDATORY',
      statusLabel: 'Mandatory (Turnover ≥ ₹5 Cr)',
      reason: `E-Invoice is mandatory because Previous FY Turnover (${formatCr(prevTurnover)}) exceeds the ₹5 Crore threshold.`,
      oneWayGateActive: true,
      threshold,
      percentageOfThreshold: 100,
      formattedPreviousTurnover: formatINR(prevTurnover),
      formattedThreshold: '₹5.00 Cr (₹50,000,000)',
    };
  }

  // 3. Manual Override Enabled
  if (manualOverride) {
    return {
      isMandatory: true,
      status: 'MANUAL_OVERRIDE',
      statusLabel: 'Mandatory (Admin Override)',
      reason: 'E-Invoice generation has been manually enforced by an administrator.',
      oneWayGateActive: false,
      threshold,
      percentageOfThreshold: percentage,
      formattedPreviousTurnover: formatINR(prevTurnover),
      formattedThreshold: '₹5.00 Cr (₹50,000,000)',
    };
  }

  // 4. Exempt / Optional (< 5 Crore)
  return {
    isMandatory: false,
    status: 'EXEMPT',
    statusLabel: 'Optional / Exempt (< ₹5 Cr)',
    reason: `Previous FY Turnover (${formatCr(prevTurnover)}) is below the ₹5 Crore mandate threshold. E-Invoice registration is optional.`,
    oneWayGateActive: false,
    threshold,
    percentageOfThreshold: percentage,
    formattedPreviousTurnover: formatINR(prevTurnover),
    formattedThreshold: '₹5.00 Cr (₹50,000,000)',
  };
}
